// One issue on a topic's list: its title, what the reader wants in one line, and the first
// question still open.
//
// A principal may reword or remove the title from the page (`actions` and `children`, filled
// in by `Decisions`). Everything else about an issue is its own page: the row says what
// exists and where it stands, the page is where it is worked on.
//
// The words of what someone wants are prose and may be long. They are not truncated, not
// clamped and not put behind "show more": cards vary in height instead.

import type { ReactNode } from 'react';

import type { Decision } from '../api/contract.js';

export type DecisionCardProps = {
  decision: Decision;
  /** The row's menu, when the reader may edit. Absent otherwise. */
  actions?: ReactNode;
  /** An edit form that takes the place of the label. */
  editor?: ReactNode;
  /** A body area under the text: the remove confirmation. */
  children?: ReactNode;
  /** Extra class while the row is lit up after a change. */
  highlightClass?: string;
  /** "Changed by Maya · 1 Oct", when the list says who last changed this topic. */
  changedNote?: string | null;
};

export function DecisionCard({
  decision,
  actions,
  children,
  editor,
  highlightClass,
  changedNote = null,
}: DecisionCardProps): JSX.Element {
  const intention = decision.intention?.trim() ?? '';
  const question = decision.openQuestion?.trim() ?? '';
  const wants = intention === '' ? null : intention;
  return (
    <li
      className={['person', 'topic', highlightClass]
        .filter((c) => c !== undefined && c !== '')
        .join(' ')}
    >
      <span className="person__identity">
        {editor ?? <span className="person__name">{decision.label}</span>}
        {wants === null ? null : (
          <span className="person__detail topic__detail">{wants}</span>
        )}
        {question === '' ? null : (
          <span className="person__detail topic__detail">
            Next to explore: {question}
          </span>
        )}
        {changedNote === null ? null : (
          <span className="person__detail person__changed">{changedNote}</span>
        )}
        {children}
      </span>
      {actions}
    </li>
  );
}
